/* Native Shopify forms remain the fallback when JavaScript is unavailable. */
(() => {
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  let cartBusy = false;
  const drawer = () => document.getElementById('pw-cart-drawer');
  const cartURL = () => document.querySelector('[data-pw-open-cart]')?.href || `${root}cart`;
  const openDialog = (dialog) => {
    if (dialog && !dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
  };

  function hasAccountSession(clientId) {
    try {
      // Presentation hint only. The account API validates access before displaying data.
      const session = JSON.parse(window.sessionStorage.getItem('pw.customer.session') || 'null');
      return Boolean(
        clientId &&
        session?.clientId === clientId &&
        session.access &&
        (session.expires > Date.now() || session.refresh),
      );
    } catch {
      return false;
    }
  }

  function syncAccountLabels(authenticated) {
    document.querySelectorAll('[data-pw-account-label]').forEach((label) => {
      let signedIn = authenticated;
      if (typeof signedIn !== 'boolean') {
        signedIn = label.dataset.nativeCustomer === 'true';
        signedIn ||= hasAccountSession(label.dataset.clientId);
      }
      label.textContent = signedIn ? 'Mi cuenta' : 'Ingresar';
    });
    document.querySelectorAll('[data-pw-account-entry]').forEach((entry) => {
      const menu = entry.querySelector('[data-pw-session-menu]');
      if (!menu) return;
      const signedIn =
        typeof authenticated === 'boolean'
          ? authenticated
          : hasAccountSession(entry.dataset.clientId);
      entry.toggleAttribute('data-pw-customer-session', signedIn);
      menu.hidden = !signedIn;
      if (!signedIn) menu.open = false;
    });
  }
  window.addEventListener('pw:account-state', (event) => {
    if (typeof event.detail?.authenticated === 'boolean')
      syncAccountLabels(event.detail.authenticated);
  });
  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-pw-session-menu][open]').forEach((menu) => {
      if (!menu.contains(event.target) || event.target.closest('a')) menu.open = false;
    });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const menu = event.target.closest('[data-pw-session-menu][open]');
    if (!menu) return;
    menu.open = false;
    menu.querySelector('summary').focus();
    event.preventDefault();
  });

  // Position the exposed dialog part without reading or mutating Shopify's shadow DOM.
  let openAccount = null;
  function positionAccount() {
    if (!openAccount?.isConnected) return;
    const trigger = openAccount.getBoundingClientRect();
    // Shopify can reserve a scrollbar gutter while its top-layer dialog is open.
    const viewportWidth =
      document.documentElement.getBoundingClientRect().right || window.innerWidth;
    const width = Math.min(380, viewportWidth - 32);
    const left = Math.max(16, Math.min(trigger.right - width, viewportWidth - width - 16));
    openAccount.style.setProperty('--pw-account-dialog-right', `${viewportWidth - left - width}px`);
    openAccount.style.setProperty(
      '--shopify-account-dialog-position-top',
      `${Math.max(0, Math.round(trigger.bottom)) + 14}px`,
    );
  }
  function repositionAccount() {
    positionAccount();
    window.requestAnimationFrame?.(positionAccount);
  }
  document.addEventListener(
    'open',
    (event) => {
      const account = event.target;
      if (!(account instanceof HTMLElement) || !account.matches('.pw-native-account')) return;
      openAccount = account;
      repositionAccount();
    },
    true,
  );
  document.addEventListener(
    'close',
    (event) => {
      if (event.target === openAccount) openAccount = null;
    },
    true,
  );
  window.addEventListener('resize', repositionAccount, { passive: true });
  document.addEventListener('scroll', repositionAccount, { capture: true, passive: true });

  function initializeTabs(scope = document) {
    scope.querySelectorAll('[data-pw-tabs]').forEach((section) => {
      if (section.classList.contains('pw-ready')) return;
      const tabs = [...section.querySelectorAll('[data-pw-tab]')];
      if (!tabs.length) return;
      section.querySelectorAll('[data-pw-tab-panel]').forEach((panel, i) => {
        panel.hidden = i !== 0;
      });
      section.classList.add('pw-ready');
    });
  }

  function initializeClaimsDate(scope = document) {
    const title = scope.querySelector(
      '.pw-help-page--claims .pw-claims-provider .pw-help-responsive-grid > div:first-child',
    );
    if (!title) return;
    let date = title.querySelector('.pw-claims-date');
    if (!date) {
      date = document.createElement('span');
      date.className = 'pw-claims-date';
      title.append(date);
    }
    // Display date only; Hulk remains responsible for the real submission timestamp.
    date.textContent = `Fecha: ${new Intl.DateTimeFormat('es-PE', {
      timeZone: 'America/Lima',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date())}`;
  }

  function initializeClaimsFallback(scope = document) {
    scope.querySelectorAll('.pw-help-page--claims').forEach((page) => {
      if (page.dataset.pwClaimsFallbackReady) return;
      page.dataset.pwClaimsFallbackReady = 'true';
      const attach = () => {
        const widget = page.querySelector('.pxFormGenerator');
        const frame = widget?.querySelector('iframe');
        if (!frame?.getAttribute('src')) return false;
        let destination;
        try {
          destination = new URL(frame.getAttribute('src'));
        } catch {
          return false;
        }
        if (
          destination.protocol !== 'https:' ||
          destination.hostname !== 'formbuilder.hulkapps.com' ||
          destination.pathname !== '/corepage/customform' ||
          destination.username ||
          destination.password
        )
          return false;
        if (!page.querySelector('.pw-claims-form-fallback')) {
          const note = document.createElement('p');
          note.className = 'pw-claims-form-fallback';
          note.append('Si el formulario no carga, ');
          const link = document.createElement('a');
          link.href = destination.href;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.textContent = 'ábrelo en otra pestaña';
          note.append(link, '.');
          widget.after(note);
        }
        return true;
      };
      if (attach()) return;
      const observer = new MutationObserver(() => {
        if (attach()) observer.disconnect();
      });
      observer.observe(page, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src'],
      });
    });
  }

  function initializeDialogs(scope = document) {
    if (typeof document.createElement('dialog').showModal !== 'function') return;
    document.documentElement.classList.add('pw-dialogs-ready');
    scope.querySelectorAll('[data-pw-open-dialog]').forEach((button) => {
      button.hidden = false;
    });
    scope.querySelectorAll('.pw-side-dialog').forEach((dialog) => {
      if (dialog.dataset.pwInitialized) return;
      dialog.dataset.pwInitialized = 'true';
      dialog.addEventListener('close', () => {
        const catalog = dialog.closest('.pw-catalog');
        const panel = dialog.querySelector('[data-pw-filter-panel]');
        if (catalog && panel) catalog.querySelector('[data-pw-filter-host]').append(panel);
        const opener = [...document.querySelectorAll('[data-pw-open-dialog]')].find(
          (button) => button.dataset.pwOpenDialog === dialog.id,
        );
        opener?.setAttribute('aria-expanded', 'false');
        opener?.focus({ preventScroll: true });
      });
    });
  }

  function selectTab(tab) {
    const section = tab.closest('[data-pw-tabs]');
    section.querySelectorAll('[data-pw-tab]').forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(item.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
  }

  function setProductImage(scope, src, alt) {
    if (!src) return;
    const holder = scope.querySelector('[data-pw-main-media] button, .pw-card-media');
    if (!holder) return;
    let image = holder.querySelector('img');
    if (!image || holder.querySelector('.pw-image-placeholder')) {
      holder.querySelector('.pw-image-placeholder')?.remove();
      image = document.createElement('img');
      image.className = 'pw-product-image';
      image.width = 600;
      image.height = 600;
      holder.prepend(image);
    }
    image.removeAttribute('srcset');
    image.src = src;
    image.alt = alt || '';
    scope.querySelectorAll('[data-pw-thumbnail]').forEach((thumbnail) => {
      const active = new URL(thumbnail.dataset.pwThumbnail, document.baseURI).href === image.src;
      thumbnail.setAttribute('aria-pressed', String(active));
    });
  }

  function selectVariant(input) {
    const form = input.form;
    const scope = form.closest('[data-pw-product], [data-pw-card]');
    const data = input.dataset;
    form.querySelectorAll('[data-pw-price]').forEach((price) => {
      price.textContent = data.price;
    });
    form.querySelectorAll('[data-pw-compare]').forEach((price) => {
      price.textContent = data.compare;
      price.hidden = data.onSale !== 'true';
    });
    const unit = form.querySelector('[data-pw-unit]');
    if (unit) unit.textContent = data.unit;
    const title = form.querySelector('[data-pw-variant-title]');
    if (title) title.textContent = data.title;
    const submit = form.querySelector('[data-pw-submit]');
    submit.disabled = data.available !== 'true';
    submit.querySelector('span').textContent = submit.disabled ? 'Agotado' : 'Agregar al carrito';
    const quantity = form.elements.quantity;
    if (quantity) {
      quantity.min = data.min || '1';
      quantity.step = data.step || '1';
      if (data.max) quantity.max = data.max;
      else quantity.removeAttribute('max');
      const min = Number(quantity.min);
      const step = Number(quantity.step);
      let value = Math.max(min, Number(quantity.value) || min);
      value = min + Math.floor((value - min) / step) * step;
      if (data.max) value = Math.min(value, Number(data.max));
      quantity.value = String(value);
    }
    const sticky = scope.querySelector('[data-pw-sticky-submit]');
    if (sticky) {
      sticky.disabled = submit.disabled;
      sticky.textContent = submit.disabled ? 'Agotado' : 'Agregar';
      scope.querySelector('[data-pw-sticky-price]').textContent = data.price;
      scope.querySelector('[data-pw-sticky-variant]').textContent = data.title;
    }
    setProductImage(scope, data.image, data.imageAlt);
    form
      .closest('[data-pw-card]')
      ?.querySelectorAll('[data-pw-product-link]')
      .forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set('variant', input.value);
        link.href = url.href;
      });
    if (form.closest('.pw-product-details')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', input.value);
      window.history.replaceState({}, '', url.href);
    }
    const message = form.querySelector('[data-pw-message]');
    if (message) message.textContent = '';
  }

  function sectionIDs() {
    const ids = ['pw-cart-drawer'];
    const page = document.querySelector('[data-pw-cart-page]');
    if (page) ids.push(page.dataset.sectionId);
    return ids;
  }

  async function renderCart(sections) {
    const ids = sectionIDs();
    if (!sections || ids.some((id) => !sections[id])) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', ids.join(','));
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('No pudimos actualizar el carrito.');
      sections = await response.json();
    }
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    if (!sections['pw-cart-drawer']) throw new Error('Carrito no disponible.');
    const renderedDrawer = parse(sections['pw-cart-drawer']);
    const content = renderedDrawer.querySelector('[data-pw-cart-content]');
    if (!content) throw new Error('Carrito no disponible.');
    const page = document.querySelector('[data-pw-cart-page]');
    const nextPage =
      page && sections[page.dataset.sectionId]
        ? parse(sections[page.dataset.sectionId]).querySelector('[data-pw-cart-page]')
        : null;
    if (page && !nextPage) throw new Error('No pudimos actualizar el resumen del carrito.');
    drawer()
      .querySelector('[data-pw-cart-content]')
      .replaceChildren(...content.childNodes);
    const count = renderedDrawer.querySelector('[data-pw-cart-count]')?.textContent || '0';
    document.querySelectorAll('[data-pw-cart-count]').forEach((badge) => {
      badge.textContent = count;
    });
    const total = renderedDrawer.querySelector('[data-pw-cart-total]')?.textContent;
    document.querySelectorAll('[data-pw-cart-subtotal]').forEach((subtotal) => {
      if (total !== undefined) subtotal.textContent = total;
      subtotal.hidden = Number(count) === 0;
    });
    document.querySelectorAll('[data-pw-cart-label]').forEach((label) => {
      label.hidden = Number(count) > 0;
    });
    if (page && nextPage) page.replaceChildren(...nextPage.childNodes);
  }

  async function requestCart(path, data) {
    const response = await fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...data,
        sections: sectionIDs(),
        sections_url: window.location.pathname,
      }),
    });
    const result = await response.json();
    if (!response.ok)
      throw new Error(
        typeof result.description === 'string'
          ? result.description
          : 'No pudimos actualizar el carrito. Revisa la cantidad disponible.',
      );
    return result;
  }

  function freezeCartForms() {
    const forms = [...document.querySelectorAll('[data-pw-cart-form]')];
    const controls = forms.flatMap((form) => [...form.querySelectorAll('input, button')]);
    const states = controls.map((control) => control.disabled);
    const reviewLinks = forms.flatMap((form) => [
      ...form.querySelectorAll('[data-pw-review-cart]'),
    ]);
    const reviewStates = reviewLinks.map((link) => link.getAttribute('aria-disabled'));
    forms.forEach((form) => form.setAttribute('aria-busy', 'true'));
    controls.forEach((control) => {
      control.disabled = true;
    });
    reviewLinks.forEach((link) => link.setAttribute('aria-disabled', 'true'));
    return () => {
      controls.forEach((control, index) => {
        control.disabled = states[index];
      });
      reviewLinks.forEach((link, index) => {
        if (reviewStates[index] === null) link.removeAttribute('aria-disabled');
        else link.setAttribute('aria-disabled', reviewStates[index]);
      });
      forms.forEach((form) => form.removeAttribute('aria-busy'));
    };
  }

  async function recoverCart(error, form) {
    const container = form.closest('[data-pw-cart-page]') || drawer();
    try {
      await renderCart();
    } catch {
      form.querySelectorAll('[data-pw-cart-quantity]').forEach((input) => {
        input.value = input.defaultValue;
      });
    }
    const message =
      container?.querySelector('[data-pw-cart-message]') ||
      container?.querySelector('[data-pw-cart-status]');
    if (message)
      message.textContent = error.message || 'Revisa tu carrito antes de volver a intentarlo.';
  }

  async function changeCart(key, quantity, form) {
    if (cartBusy || !Number.isSafeInteger(quantity) || quantity < 0) return;
    cartBusy = true;
    const focused = document.activeElement;
    const focusedKey =
      focused?.dataset?.key ||
      focused?.closest('.pw-cart-line')?.querySelector('[data-key]')?.dataset.key;
    const unfreeze = freezeCartForms();
    document.querySelectorAll('[data-pw-cart-status]').forEach((status) => {
      status.textContent = '';
    });
    try {
      const result = await requestCart('cart/change.js', { id: key, quantity });
      try {
        await renderCart(result.sections);
      } catch {
        window.location.assign(cartURL());
        return;
      }
      const container = drawer()?.open ? drawer() : document.querySelector('[data-pw-cart-page]');
      const nextInput = [...(container?.querySelectorAll('[data-key]') || [])].find(
        (input) => input.dataset.key === focusedKey,
      );
      (nextInput || container?.querySelector('button, a'))?.focus({ preventScroll: true });
    } catch (error) {
      await recoverCart(error, form);
    } finally {
      cartBusy = false;
      unfreeze();
    }
  }

  document.addEventListener('click', async (event) => {
    const target = event.target.closest('a, button');
    if (!target) return;
    if (target.matches('a') && target.closest('.pw-menu-dialog')) {
      target.closest('dialog').close();
    }
    if (target.matches('[data-pw-open-dialog]')) {
      const dialog = document.getElementById(target.dataset.pwOpenDialog);
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const catalog = dialog.closest('.pw-catalog');
      const panel = catalog?.querySelector('[data-pw-filter-panel]');
      if (panel) {
        panel.open = true;
        dialog.querySelector('[data-pw-filter-target]').append(panel);
      }
      target.setAttribute('aria-expanded', 'true');
      openDialog(dialog);
      return;
    }
    if (target.matches('[data-pw-review-cart]') && cartBusy) {
      event.preventDefault();
      return;
    }
    if (
      target.matches('[data-pw-open-cart]') &&
      drawer() &&
      typeof drawer().showModal === 'function' &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey
    ) {
      event.preventDefault();
      if (cartBusy) return;
      cartBusy = true;
      target.setAttribute('aria-busy', 'true');
      try {
        await renderCart();
        openDialog(drawer());
      } catch {
        window.location.assign(cartURL());
      } finally {
        cartBusy = false;
        target.removeAttribute('aria-busy');
      }
    } else if (target.matches('[data-pw-close]')) {
      target.closest('dialog')?.close();
    } else if (target.matches('[data-pw-tab]')) {
      selectTab(target);
    } else if (target.matches('[data-pw-thumbnail]')) {
      const scope = target.closest('[data-pw-product]');
      setProductImage(scope, target.dataset.pwThumbnail, target.dataset.alt);
      scope
        .querySelectorAll('[data-pw-thumbnail]')
        .forEach((item) => item.setAttribute('aria-pressed', String(item === target)));
    } else if (target.matches('[data-pw-quantity]')) {
      if (cartBusy && target.closest('[data-pw-cart-form]')) return;
      const input = target.parentElement.querySelector('input');
      const direction = Number(target.dataset.pwQuantity);
      const step = Number(input.step) || 1;
      const min = Number(input.min) || 0;
      const max = input.max ? Number(input.max) : Infinity;
      const value = Math.min(max, Math.max(min, Number(input.value) + direction * step));
      if (value !== Number(input.value)) {
        input.value = String(value);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (target.matches('[data-pw-remove]')) {
      event.preventDefault();
      changeCart(target.dataset.pwRemove, 0, target.closest('form'));
    } else if (target.matches('[data-pw-zoom]')) {
      const image = target.querySelector('img');
      const dialog = document.getElementById('pw-zoom-dialog');
      if (image && dialog) {
        const zoom = image.cloneNode();
        zoom.removeAttribute('srcset');
        zoom.removeAttribute('class');
        dialog.querySelector('[data-pw-zoom-image]')?.remove();
        zoom.dataset.pwZoomImage = '';
        dialog.append(zoom);
        openDialog(dialog);
      }
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target.matches('[data-pw-variant]')) selectVariant(target);
    if (target.matches('[data-pw-sort]')) target.form?.requestSubmit();
    if (target.matches('[data-pw-cart-quantity]')) {
      if (target.value.trim() === '' || !target.checkValidity()) {
        target.value = target.defaultValue;
        target.form.querySelector('[data-pw-cart-message]').textContent =
          'Ingresa una cantidad válida.';
        return;
      }
      changeCart(target.dataset.key, Number(target.value), target.form);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (
      !event.target.matches('[data-pw-tab]') ||
      !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
    )
      return;
    event.preventDefault();
    const tabs = [...event.target.closest('[role=tablist]').querySelectorAll('[data-pw-tab]')];
    const current = tabs.indexOf(event.target);
    const index =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    selectTab(tabs[index]);
    tabs[index].focus();
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (form.matches('[data-pw-cart-form]') && cartBusy) {
      event.preventDefault();
      return;
    }
    if (!form.matches('[data-pw-add]') || !drawer() || typeof drawer().showModal !== 'function')
      return;
    const data = new FormData(form);
    if ([...data.values()].some((value) => value instanceof File && value.size > 0)) return;
    event.preventDefault();
    if (form.getAttribute('aria-busy') === 'true' || cartBusy) return;
    const button = form.querySelector('[data-pw-submit]');
    const message = form.querySelector('[data-pw-message]');
    const item = { id: data.get('id'), quantity: Number(data.get('quantity') || 1) };
    if (data.get('selling_plan')) item.selling_plan = data.get('selling_plan');
    const properties = Object.create(null);
    for (const [name, value] of data) {
      if (name.startsWith('properties[') && name.endsWith(']') && typeof value === 'string')
        properties[name.slice(11, -1)] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    cartBusy = true;
    const unfreeze = freezeCartForms();
    form.setAttribute('aria-busy', 'true');
    button.disabled = true;
    message.textContent = 'Agregando…';
    try {
      const result = await requestCart('cart/add.js', { items: [item] });
      message.textContent = '¡Agregado al carrito!';
      try {
        await renderCart(result.sections);
        openDialog(drawer());
      } catch {
        window.location.assign(cartURL());
      }
    } catch (error) {
      try {
        await renderCart();
      } catch {
        /* Keep the last confirmed view; never retry a mutation automatically. */
      }
      message.textContent =
        error.message ||
        'No pudimos confirmar la operación. Revisa tu carrito antes de volver a intentarlo.';
    } finally {
      cartBusy = false;
      unfreeze();
      form.removeAttribute('aria-busy');
      const selected = form.querySelector('[data-pw-variant]:checked');
      button.disabled = selected ? selected.dataset.available !== 'true' : false;
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target.matches('dialog[data-pw-dialog]')) {
      const box = event.target.getBoundingClientRect();
      if (
        event.clientX < box.left ||
        event.clientX > box.right ||
        event.clientY < box.top ||
        event.clientY > box.bottom
      )
        event.target.close();
    }
  });
  document.addEventListener('shopify:section:load', (event) => {
    syncAccountLabels();
    initializeTabs(event.target);
    initializeDialogs(event.target);
    initializeClaimsDate(event.target);
    initializeClaimsFallback(event.target);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target.closest('[data-pw-tab-panel]');
    const tab = panel && document.getElementById(panel.getAttribute('aria-labelledby'));
    if (tab) selectTab(tab);
  });
  window.addEventListener('pageshow', (event) => {
    syncAccountLabels();
    initializeClaimsDate();
    if (event.persisted)
      renderCart().catch(() => {
        /* Native cart link remains available. */
      });
  });
  initializeTabs();
  syncAccountLabels();
  initializeDialogs();
  initializeClaimsDate();
  initializeClaimsFallback();
  window.matchMedia?.('(min-width: 750px)').addEventListener('change', (event) => {
    if (event.matches)
      document.querySelectorAll('.pw-side-dialog[open]').forEach((dialog) => dialog.close());
  });
})();
